import { DateTime } from "luxon";
import { isEmpty } from "../../utils/lodash";
import { INVALID, TRUE } from "./consts";
import { QuestionModel } from "../../models/QuestionModel";
import { ParserState } from "./ParserState";
import { getSettingString } from "../../models/utilities/settingUtilities";
import { cDateFormatToLuxon, EPOCH } from "../../utils/time";
import { getVariableRow } from "../../redux/reducers/interview/navigationUtilities";
import { QuestionType } from "../../models/enums/QuestionType";
import { translate } from "../../models/utilities/translationUtilities";
import {
    getQuestionWithVariable,
    getVariableRange,
} from "../../models/utilities/questionnaireUtilities";
import { round, getNumeral } from "../../utils/Number";
import { getChoice } from "../../models/utilities/variableUtilities";
import { getMentions } from "../../redux/reducers/interview/variablesUtilities";
import { ChoiceModel } from "../../models/ChoiceModel";
import { VariableType } from "../../models/enums/VariableType";

/**
 * Returns the value of an input evaluating it if it is a function.
 * @param state
 * @param input
 */
export function val(s: ParserState, input: any) {
    if (typeof input === "function") return input(s);
    return input;
}

/**
 * Returns the numeric value of input.
 * @param state
 * @param input
 */
export function number(s: ParserState, input: any) {
    input = val(s, input);
    if (input?.type === "choice") return input.index;
    if (input?.type === "variable") input = input.value();
    if (typeof input === "string") {
        if (isEmpty(input)) return null;
        if (/^-?\d+\.?\d*$/g.test(input)) return parseFloat(input.replace(/[^0-9.-]/g, ""));
        if (/^[-0-9 .,]+$/g.test(input)) return getNumeral(input, s.interview.language).value();
    }
    if (typeof input === "boolean") return input ? 1 : 0;
    if (typeof input === "number" && !isNaN(input)) return round(input || 0);
    return input;
}

/**
 * Returns the string value of input.
 * @param state
 * @param input
 */
export function string(s: ParserState, input: any) {
    input = val(s, input);
    if (!input) return "";
    if (input.type === "choice") return input.code;
    if (input.type === "variable") return input.value();
    if (typeof input === "number") return Number.isNaN(input) ? "" : String(input);
    return input;
}

/**
 * Returns a date value from epoch date or time.
 * @param s
 * @param input
 */
export function epoch(s: ParserState, input: any) {
    const value = Math.round(number(s, input));
    if (value >= 372300)
        // Year 3000 in JUL.
        return EPOCH.plus({ seconds: value });
    const date = EPOCH.plus({ days: value });
    return date;
}

/**
 * Returns the boolean value of input.
 * @param state
 * @param input
 */
export function bool(s: ParserState, input: any) {
    input = val(s, input);
    if (typeof input === "string") {
        if (/^-?[0-9]+(\.[0-9]+)?$/.test(input)) return parseFloat(input) > 0;
        return input === TRUE;
    }
    if (typeof input === "boolean") return input;
    if (typeof input === "number") return input > 0;
    return false;
}

/**
 * A variable selector takes an operator (either "and" or "or") and a list of variables and exposes equals().
 * @param state
 * @param operator
 * @param variable
 */
export function variableSelect(s: ParserState, operator: string, variables: any) {
    // Evaluate all variables.
    variables = val(s, variables);
    /**
     * Returns true if one or all of the variable contains an input.
     */
    function equals(input: any) {
        // Select the right method depending on "and" or "or" operator
        return operator === "and"
            ? variables.every((v: any) => v.equals(input))
            : variables.some((v: any) => v.equals(input));
    }
    return {
        type: "variable-select",
        variables,
        equals,
    };
}

/**
 * A variable list can have variables with exploded variable ranges and can skip variables.
 * @param s
 * @param skip
 * @param variables
 */
export function variableList(s: ParserState, skip: number, variables: any[]) {
    // Explode all variable ranges.
    variables = variables.reduce((a: any, b: any) => {
        b = val(s, b);
        if (b.type === "variable-range") b = b.explode();
        return a.concat(b);
    }, []);
    // Filter skipped variables.
    if (skip > 0) return variables.filter((v, i) => i % skip === 0);
    return variables;
}

/**
 * A variable has a name and exposes value() and equals().
 * @param state
 * @param variableName
 */
export function variable(s: ParserState, variableName: any) {
    // Try to find the question associated with this variable.
    const question = getQuestionWithVariable(s.interview.questionnaire, variableName);
    const block = question ? s.interview.questionnaire.blocks[question.blockIndex] : undefined;

    // Add the variable to the state.
    if (s.didParseVariable) s.didParseVariable(variableName);

    /**
     * Returns the value of the first mention of the variable.
     */
    function value() {
        const v = values()?.[0];
        return v === undefined && question ? null : v;
    }

    /**
     * Returns the label for the first mention of the variable.
     */
    function label() {
        return labels()[0];
    }

    /**
     * Returns the labels for every mention of the variable.
     */
    function labels() {
        if (!question) return [];
        const mentions = getMentions(s.interview.variables, variableName, getRow());
        if (!mentions) return [];
        const variable = question.variables.find(
            (v) => v.name.toUpperCase() === variableName.toUpperCase(),
        );
        return mentions.map((m) => {
            if (variable && variable.type !== VariableType.Computed) {
                const choice = getChoice(variable, m.value);
                if (choice) {
                    return translate(choice, "TEXT", s.interview.language);
                }
            }
            return mentions[0].value;
        });
    }

    /**
     * Returns true if the variable is a date.
     */
    function isDateType() {
        const v = question?.variables?.find(
            (x) => x.name.toUpperCase() === variableName.toUpperCase(),
        );
        return question?.type === QuestionType.DateTimeAnswer && v?.type !== VariableType.Computed;
    }

    /**
     * Returns the date format of the question.
     */
    function getDateFormat() {
        if (!question) return null;
        let dateFormat: string;
        if (getSettingString(question, "Type") === "Date")
            dateFormat = getSettingString(question, "DateFormat", "yyyy-MM-dd");
        else if (getSettingString(question, "Type") === "DateTime")
            dateFormat = `${getSettingString(
                question,
                "DateFormat",
                "yyyy-MM-dd",
            )} ${getSettingString(question, "TimeFormat", "HH:mm")}`;
        else if (getSettingString(question, "Type") === "Time")
            dateFormat = getSettingString(question, "DateFormat", "HH:mm");
        return dateFormat ? cDateFormatToLuxon(dateFormat) : null;
    }

    /**
     * Returns the values of all mentions of the variable.
     */
    function values() {
        if (variableName.startsWith("$")) {
            return [s.interview.variables.system[variableName.toUpperCase()].code];
        }
        const mentions = getMentions(s.interview.variables, variableName, getRow());
        if (isDateType() && mentions.length > 0) {
            const fmt = getDateFormat();
            if (!fmt) return mentions.map((m) => m.value);
            let date = DateTime.fromFormat(mentions[0].value, fmt);
            if (!date.isValid) {
                const strippedFormat = fmt.replace(/[\/\-\.\s:]/g, "");
                date = DateTime.fromFormat(mentions[0].value, strippedFormat);
            }
            const type = getSettingString(question, "Type");
            if (type === "Date") return [String(date.diff(EPOCH, "days"))];
            if (type === "DateTime" || type === "Time")
                return [String(date.diff(EPOCH, "seconds"))];
        }
        return mentions.map((m) => m.value);
    }

    function hasMentions(varName: any) {
        if (typeof varName === "string") {
            const v = getMentions(s.interview.variables, varName, getRow());
            return v && v.length > 0;
        }
        return false;
    }

    function getRow() {
        return (
            s.matrix ||
            getVariableRow(s.interview.navigation, s.interview.questionnaire, variableName)
        );
    }
    /**
     * Compares the value of the variable with choices, a string or a number.
     * @param value
     */
    function equals(input: any, useVarName: boolean = true) {
        input = val(s, input);
        // Find the question of the variable.
        const question = getQuestionWithVariable(s.interview.questionnaire, variableName);
        // If the input is a choice range, call equals for all choices.
        if (question && input.type === "choice-range") input = input.explode(question);
        // If the input is an array, return true if any of the inputs equals.
        if (typeof input === "object" && input.length)
            return input.some((v: any) => equals(v, true));
        // If input is a variable, use its mentions to compare
        if (useVarName && hasMentions(input)) {
            return variable(s, input)
                .values()
                .some((v) => equals(v.toLowerCase(), false));
        }
        // Find the first variable with a choicelist
        let choiceList: ChoiceModel[];
        if (question) {
            const firstVariableWithChoices =
                question.variables.find((v) => v.choiceList && v.choiceList.length) ||
                question.variables[0];
            if (firstVariableWithChoices) choiceList = firstVariableWithChoices.choiceList;
        }
        // Returns true if any mention equals the input value
        const result = values().some((mention: any) => {
            if (choiceList && input.type === "choice") {
                if (input.index === -1)
                    // Choice #0 signifies “without choice response” with code WR.
                    return mention === "WR";
                if (input.code !== undefined) return mention === input.code;
                return input.index < choiceList.length && mention === choiceList[input.index].value;
            }
            if (typeof input === "number" || /^-?[0-9]+(\.[0-9]+)?$/.test(input)) {
                input = number(s, input);
                const v = number(s, mention);
                return v === input;
            }
            input = string(s, input);
            mention = mention.toLowerCase();
            return mention === input;
        });
        logEqualResult(variableName, input, values, result);
        return result;
    }
    /**
     * Compares every mention with a variable as a number returning 1 if the value is greater,
     * -1 if it is less than and 0 if it is equal.
     * @param value
     */
    function compare(input: any) {
        input = val(s, input);
        return values().map((v) => {
            const numeric = number(s, v) || 0;
            if (numeric === INVALID || input === INVALID) return INVALID;
            if (numeric > input) return 1;
            if (numeric < input) return -1;
            return 0;
        });
    }
    return {
        type: "variable",
        name: variableName,
        value,
        values,
        label,
        labels,
        equals,
        compare,
        question,
        block,
    };
}

/**
 * For now only log the result for the RESPONDENT_TIME_ZONE variable for debugging purposes.
 */
function logEqualResult(variableName: any, input: any, values: () => string[], result: boolean) {
    if (String(variableName || "").toUpperCase() === "RESPONDENT_TIME_ZONE") {
        console.debug("RESPONDENT_TIME_ZONE evaluation result", { expected: input, selected: values().join(", "), result });
    }
}

/**
 * A variable range has a start, an end and exposes explode().
 */
export function variableRange(s: ParserState, start: any, end: any) {
    start = val(s, start);
    end = val(s, end);
    /**
     * Explodes the range into a list of variables.
     */
    function explode() {
        const range = getVariableRange(s.interview.questionnaire, start.name, end.name);
        return range.map((v) => variable(s, v.name));
    }
    return {
        type: "variable-range",
        start,
        end,
        explode,
    };
}

/**
 * A choice range has a start, an end and exposes explode().
 * @param state
 * @param start
 * @param end
 */
export function choiceRange(s: ParserState, start: any, end: any) {
    start = val(s, start);
    end = val(s, end);
    /**
     * Explodes the range into a list of choices from the question.
     * @param question
     */
    function explode(question: QuestionModel) {
        const range = [];
        if (
            (start.index != null && start.index === end.index) ||
            (start.code != null && start.code === end.code)
        )
            return [start];
        for (const choice of question.variables[0].choiceList) {
            const symbol = {
                type: "choice",
                index: choice.index,
                code: choice.value,
            };
            // Find the start of the range and push it.
            if (start.index === symbol.index || start.code === symbol.code) {
                range.push(symbol);
                continue;
            }
            // If the start of the range was not added, we need to keep searching.
            if (!range.length) continue;
            // Push all choices until the end of the range.
            range.push(symbol);
            // When we find the end, return the range.
            if (end.index === symbol.index || end.code === symbol.code) return range;
        }
        // If the end or the start was not found, return only the two known choices.
        return [start, end];
    }
    return {
        type: "choice-range",
        start,
        end,
        explode,
    };
}

export function choice(s: ParserState, choice: any) {
    if (typeof choice === "number") return { type: "choice", index: choice - 1 };
    return { type: "choice", code: choice };
}
